// 🏎️ Sepang International Circuit: 2.71195° N, 101.73829° E
import React, { Suspense, lazy, useEffect, useState, useRef } from "react";
import { HelmetProvider, Helmet } from "react-helmet-async";
import {
  Link,
  Navigate,
  Route,
  Routes,
  useNavigate,
  useParams,
  useLocation,
} from "react-router-dom";
import AdminDashboard from "./components/AdminDashboard";
import FirstTimeLogin from "./components/FirstTimeLogin";
import HotPlates from "./components/HotPlates";
import ProfilePage from "./components/ProfilePage";
import SearchBar from "./components/SearchBar";
import VehicleProfile from "./components/VehicleProfile";
import { API_ENDPOINTS, AUTH_ENDPOINTS } from "./config/constants";
import ExplorePage from "./components/ExplorePage";
import AnnouncementsManager from "./components/AnnouncementsManager";
import WhatsG80 from "./components/WhatsG80";
import NotFound from "./components/NotFound";
import "./App.css";
import { Toaster } from "react-hot-toast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faIdCard, faFire, faSignOut } from "@fortawesome/free-solid-svg-icons";
import NotificationBell from "./components/NotificationBell";
import LikedPlatesButton from "./components/LikedPlatesButton";
import LikedPlates from "./components/LikedPlates";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "./components/LanguageSwitcher";
import QuickSubmitButton from "./components/QuickSubmitButton";
import LogoPlate from "./components/LogoPlate";
import ThemeToggle from "./components/ThemeToggle";

// Lazy load static content
const LegalPage = lazy(() => import("./components/LegalPage"));
const CommunityGuidelines = lazy(() =>
  import("./components/CommunityGuidelines")
);

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="text-center p-4">
          <h2 className="text-xl font-bold text-red-600 dark:text-red-400">
            Something went wrong
          </h2>
          <button
            className="mt-2 px-4 py-2 bg-blue-500 text-white rounded dark:bg-blue-600 dark:hover:bg-blue-700"
            onClick={() => window.location.reload()}
          >
            Reload Page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

// Vehicle page component that handles fetching vehicle data
const VehiclePage = ({ user }) => {
  const { plate } = useParams();
  const [vehicle, setVehicle] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const vehicleContentRef = useRef(null);
  const isFirstRender = useRef(true);

  // Handle scroll on both direct access and navigation
  useEffect(() => {
    // Skip first render for direct access to let content load
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    // Check for comment hash in URL
    const hash = location.hash;
    if (hash && hash.startsWith("#comment-")) {
      const commentId = hash.replace("#comment-", "");
      // Wait for content to be rendered
      setTimeout(() => {
        const commentElement = document.getElementById(`comment-${commentId}`);
        if (commentElement) {
          commentElement.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
          // Add a highlight effect
          commentElement.classList.add("bg-yellow-50");
          setTimeout(() => {
            commentElement.classList.remove("bg-yellow-50");
            commentElement.classList.add("bg-transparent");
          }, 2000);
        }
      }, 100);
    } else if (vehicleContentRef.current) {
      vehicleContentRef.current.scrollIntoView({
        behavior: location.key ? "smooth" : "auto", // smooth only if we have navigation history
        block: "start",
      });
    }
  }, [vehicle, location]);

  useEffect(() => {
    const fetchVehicle = async () => {
      try {
        const res = await fetch(
          API_ENDPOINTS.VEHICLE_BY_PLATE(encodeURIComponent(plate)),
          {
            credentials: "include",
          }
        );
        if (res.status === 404) {
          const data = await res.json();
          setError(data.msg);
          setVehicle(null);
          return;
        }
        const data = await res.json();
        setVehicle(data);
        setError(null);
      } catch (err) {
        console.error("Error fetching vehicle", err);
        setError("An unexpected error occurred. Please try again.");
      }
    };

    if (plate) {
      fetchVehicle();
    }
  }, [plate]);

  return (
    <>
      <SearchBar
        onSearch={(searchPlate) => navigate(`/plate/${searchPlate}`)}
      />
      <div ref={vehicleContentRef} className="scroll-mt-4 mt-4">
        {vehicle ? (
          <VehicleProfile vehicle={vehicle} user={user} />
        ) : (
          <div className="mt-4 bg-gray-100 text-gray-700 p-3 rounded dark:bg-gray-800 dark:text-gray-300">
            {error ? (
              <div className="bg-blue-50 text-blue-800 p-4 rounded-lg shadow-sm border border-blue-100 dark:bg-blue-900 dark:text-blue-100 dark:border-blue-800">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-2xl">🔍</span>
                  <span>{error}</span>
                </div>
                {!user && (
                  <div className="mt-2 bg-white/50 p-3 rounded-md backdrop-blur-sm dark:bg-gray-800 dark:text-gray-300">
                    <a
                      href={`${
                        AUTH_ENDPOINTS.GOOGLE_LOGIN
                      }?returnTo=${encodeURIComponent(
                        window.location.pathname + window.location.search
                      )}`}
                      className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium transition-colors dark:text-blue-400 dark:hover:text-blue-600"
                      onClick={(e) => {
                        const returnPath =
                          window.location.pathname + window.location.search;
                        sessionStorage.setItem("preLoginPath", returnPath);
                        localStorage.setItem("preLoginPath", returnPath);
                      }}
                    >
                      <span aria-hidden="true">→</span>
                      Join the community and start your carspotting adventure!
                    </a>
                  </div>
                )}
              </div>
            ) : (
              "Loading..."
            )}
          </div>
        )}
      </div>
      {vehicle && (
        <HotPlates
          onSearch={(searchPlate) => navigate(`/plate/${searchPlate}`)}
          autoHide={!!vehicle}
        />
      )}
    </>
  );
};

// Home page component
const HomePage = ({ user }) => {
  const navigate = useNavigate();

  return (
    <>
      <SearchBar onSearch={(plate) => navigate(`/plate/${plate}`)} />
      <HotPlates
        onSearch={(plate) => navigate(`/plate/${plate}`)}
        autoHide={false}
      />
    </>
  );
};

function App() {
  const [user, setUser] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [initialDAudio, setInitialDAudio] = useState(null);
  const navigate = useNavigate();
  const [isInverted, setIsInverted] = useState(false);
  const konamiSequence = [
    "ArrowUp",
    "ArrowUp",
    "ArrowDown",
    "ArrowDown",
    "ArrowLeft",
    "ArrowRight",
    "ArrowLeft",
    "ArrowRight",
    "b",
    "a",
  ];
  const [currentSequence, setCurrentSequence] = useState([]);
  const location = useLocation();
  const { t } = useTranslation();

  useEffect(() => {
    console.log(`
⠀⠀⠀⠀⠀⠀⠀⢀⣠⣤⣤⠶⢶⡶⠶⣤⣤⣄⡀⠀⠀⠀⠀⠀⠀⠀
⠀⠀⠀⠀⢀⣤⣾⣿⣿⣿⣿⠀⡸⢇⠀⣿⣿⣿⣿⣷⣤⡀⠀⠀⠀⠀
⠀⠀⠀⣴⢏⡉⠻⢿⣿⣿⣿⠤⠧⠼⠤⣿⣿⣿⣿⠟⢩⠿⣦⠀⠀⠀
⠀⢀⣾⣅⠘⢡⠆⡴⠛⢉⣠⣤⣶⠀⠀⠀⠉⠛⢯⣠⠔⠠⠚⣷⡀⠀
⠀⣾⣿⣿⣷⣦⡞⢀⣴⣿⣿⣿⣿⠀⠀⠀⠀⠀⠀⢻⣤⣶⣿⣿⣷⠀
⢠⣿⣿⣿⣿⡟⠀⣾⣿⣿⣿⣿⣿⠀⠀⠀⠀⠀⠀⠀⢻⣿⣿⣿⣿⡄
⢸⣿⣿⣿⣿⡇⠘⠛⠛⠛⠛⠛⠛⣤⣤⣤⣤⣤⣤⡄⢸⣿⣿⣿⣿⡇
⠘⣿⣿⣿⣿⣧⠀⠀⠀⠀⠀⠀⠀⣿⣿⣿⣿⣿⡿⠀⣼⣿⣿⣿⣿⠃
⠀⢿⣿⣿⣿⣿⣧⠀⠀⠀⠀⠀⠀⣿⣿⣿⣿⠟⠁⣼⣿⣿⣿⣿⡿⠀
⠀⠈⢿⣿⣿⣿⣿⣷⣤⣀⠀⠀⠀⠿⠛⠋⣁⣤⣾⣿⣿⣿⣿⡿⠁⠀
⠀⠀⠀⠻⣿⣿⣿⣿⣿⣿⣿⣶⣶⣶⣶⣿⣿⣿⣿⣿⣿⣿⠟⠀⠀⠀
⠀⠀⠀⠀⠈⠛⢿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⡿⠛⠁⠀⠀⠀⠀
⠀⠀⠀⠀⠀⠀⠀⠈⠙⠛⠛⠿⠿⠿⠿⠛⠛⠋⠁⠀⠀⠀⠀⠀⠀⠀
⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀

Welcome to G80 Or Not! Happy hunting! 🏎️
`);

    const fetchUser = async () => {
      try {
        const response = await fetch(AUTH_ENDPOINTS.CURRENT_USER, {
          credentials: "include",
        });

        if (!response.ok) {
          setUser(null);
          setIsLoggedIn(false);
          setIsLoading(false);
          return;
        }

        const text = await response.text();
        if (!text) {
          setUser(null);
          setIsLoggedIn(false);
          setIsLoading(false);
          return;
        }

        try {
          const data = JSON.parse(text);
          setUser(data);
          setIsLoggedIn(true);
          // Redirect to setup if it's first login, unless viewing guidelines
          if (
            data.isFirstLogin &&
            !window.location.pathname.includes("community-guidelines")
          ) {
            navigate("/setup");
          }
        } catch (parseError) {
          console.error("Error parsing user data:", parseError);
          setUser(null);
          setIsLoggedIn(false);
        }
      } catch (err) {
        console.error("Error fetching user:", err);
        setUser(null);
        setIsLoggedIn(false);
      } finally {
        setIsLoading(false);
      }
    };
    fetchUser();
  }, [navigate]);
  useEffect(() => {
    const sequences = {
      konami: {
        keys: konamiSequence,
        action: () => setIsInverted((prev) => !prev),
        join: ",",
      },
      family: {
        keys: ["f", "a", "m", "i", "l", "y"],
        action: () => {
          const toast = document.createElement("div");
          toast.textContent = "I don't have friends, I got family 🏎️";
          toast.style.cssText = `
            position: fixed;
            bottom: 20px;
            left: 50%;
            transform: translateX(-50%);
            background: rgba(0, 0, 0, 0.8);
            color: white;
            padding: 10px 20px;
            border-radius: 20px;
            z-index: 1000;
            transition: opacity 0.3s;
          `;
          document.body.appendChild(toast);
          setTimeout(() => {
            toast.style.opacity = "0";
            setTimeout(() => toast.remove(), 3000);
          }, 3000);
        },
        join: "",
      },
      tofu: {
        keys: ["t", "o", "f", "u"],
        action: () => {
          // Open YouTube video in new tab
          setTimeout(() => {
            window.open(
              "https://www.youtube.com/watch?v=-wdR-fBr8oo",
              "_blank"
            );
          }, 3000);

          // Add toast notification
          const toast = document.createElement("div");
          toast.innerHTML = `
            <div style="display: flex; align-items: center; gap: 8px;">
              <span style="font-size: 1.5em;">🏎️</span>
              <div>
                <div style="font-weight: bold; color: #ff6b6b; font-size: 1.2em; text-shadow: 2px 2px 4px rgba(0,0,0,0.3);">DEJA VU!</div>
                <div style="font-size: 0.8em; color: #ff8787;">I've just been in this place before...</div>
              </div>
            </div>
          `;
          toast.style.cssText = `
            position: fixed;
            bottom: 20px;
            left: 50%;
            transform: translateX(-50%);
            background: rgba(0, 0, 0, 0.95);
            color: white;
            padding: 12px 24px;
            border-radius: 20px;
            z-index: 10000;
            transition: all 0.3s;
            font-family: "Comic Sans MS", cursive;
            border: 2px solid #ff6b6b;
            box-shadow: 0 0 15px rgba(255, 107, 107, 0.3);
          `;

          document.body.appendChild(toast);

          // Remove toast after delay
          setTimeout(() => {
            toast.style.transform = "translateX(-50%) translateY(-20px)";
            toast.style.opacity = "0";
            setTimeout(() => toast.remove(), 300);
          }, 3000);
        },
        join: "",
      },
    };

    const handleKeyDown = (e) => {
      setCurrentSequence((seq) => {
        const newSeq = [...seq, e.key];

        // Check each sequence
        for (const { keys, action, join } of Object.values(sequences)) {
          const slicedSeq = newSeq.slice(-keys.length);
          if (slicedSeq.join(join) === keys.join(join)) {
            action();
          }
        }

        // Keep sequence length limited to longest pattern
        const maxLength = Math.max(
          ...Object.values(sequences).map((s) => s.keys.length)
        );
        return newSeq.slice(-maxLength);
      });
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      if (initialDAudio) {
        initialDAudio.pause();
        initialDAudio.currentTime = 0;
      }
    };
  }, [initialDAudio]);

  // Define meta information based on route
  const getMetaInfo = () => {
    const baseMeta = {
      title: "G80 Or Not? - Malaysia's #1 Carspotting Community",
      description:
        "Join Malaysia's premier carspotting community. Share, verify, and discover vehicles across Malaysia. A community-driven platform for car enthusiasts.",
      image: "/favicon/android-chrome-512x512.png",
      url: "https://g80ornot.com" + location.pathname,
    };

    switch (location.pathname) {
      case "/":
        return baseMeta;
      case "/explore":
        return {
          ...baseMeta,
          title: "Explore Vehicles - G80 Or Not?",
          description:
            "Discover and explore vehicles spotted across Malaysia. Filter by most discussed, recent additions, and more.",
        };
      case "/whats-g80":
        return {
          ...baseMeta,
          title: "What's G80? - Our Story",
          description:
            "Learn about G80 Or Not? - how a Reddit comment about a fake G80 M3 inspired Malaysia's first community-driven vehicle information platform.",
        };
      default:
        if (location.pathname.startsWith("/plate/")) {
          return {
            ...baseMeta,
            title: `${location.pathname.split("/")[2]} - G80 Or Not?`,
            description: `View details and discussions about vehicle ${
              location.pathname.split("/")[2]
            } on Malaysia's premier carspotting community.`,
          };
        }
        return baseMeta;
    }
  };

  const metaInfo = getMetaInfo();

  if (isLoading) {
    return <div className="p-4 text-center">{t("common.loading")}</div>;
  }

  return (
    <HelmetProvider>
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <ErrorBoundary>
          <div className="flex flex-col min-h-screen">
            {/* Show loading state while initial auth check is happening */}
            {isLoading ? (
              <div className="flex items-center justify-center min-h-screen">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500 dark:border-blue-400"></div>
              </div>
            ) : (
              <>
                <Toaster position="bottom-center" />
                <LanguageSwitcher />
                <Helmet>
                  <title>{metaInfo.title}</title>
                  <meta name="description" content={metaInfo.description} />

                  {/* Open Graph / Facebook */}
                  <meta property="og:type" content="website" />
                  <meta property="og:url" content={metaInfo.url} />
                  <meta property="og:title" content={metaInfo.title} />
                  <meta
                    property="og:description"
                    content={metaInfo.description}
                  />
                  <meta property="og:image" content={metaInfo.image} />

                  {/* Twitter */}
                  <meta property="twitter:card" content="summary_large_image" />
                  <meta property="twitter:url" content={metaInfo.url} />
                  <meta property="twitter:title" content={metaInfo.title} />
                  <meta
                    property="twitter:description"
                    content={metaInfo.description}
                  />
                  <meta property="twitter:image" content={metaInfo.image} />

                  {/* Additional SEO tags */}
                  <link rel="canonical" href={metaInfo.url} />
                  <meta
                    name="keywords"
                    content="cars, vehicles, Malaysia, carspotting, automotive, community, G80, BMW, vehicle verification"
                  />
                  <meta name="author" content="G80 Or Not?" />

                  {/* Mobile specific */}
                  <meta name="format-detection" content="telephone=no" />
                  <meta name="theme-color" content="#0ea5e9" />
                  <meta name="color-scheme" content="light dark" />

                  {/* Robots */}
                  <meta name="robots" content="index, follow" />
                </Helmet>

                <div style={{ filter: isInverted ? "invert(1)" : "none" }}>
                  <div className="App">
                    <AnnouncementsManager />
                    <header className="bg-gradient-to-r from-primary-700 to-primary-800 dark:from-primary-800 dark:to-primary-900 text-white shadow-md transition-colors duration-300 dark:bg-gray-900">
                      <div className="max-w-7xl mx-auto px-4 py-4">
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                          <div className="flex flex-col items-center md:items-start">
                            <div className="flex items-center gap-3">
                              <LogoPlate />
                            </div>
                            <p className="text-sm text-primary-200 mt-1 dark:text-primary-300">
                              {t("nav.tagline")}
                            </p>
                          </div>
                          <div className="flex flex-col md:flex-row items-center gap-4">
                            <div className="flex items-center gap-3">
                              <LanguageSwitcher />
                              <ThemeToggle />
                            </div>
                            <nav className="flex flex-wrap items-center justify-center gap-2">
                              {location.pathname === "/" ? (
                                <span className="px-3 py-2 rounded-md bg-primary-600/30 text-white font-medium dark:bg-primary-600/30 dark:text-white">
                                  {t("nav.home")}
                                </span>
                              ) : (
                                <Link
                                  className="px-3 py-2 rounded-md text-primary-100 hover:text-white hover:bg-primary-600/30 transition-colors dark:text-primary-100 dark:hover:text-white dark:hover:bg-primary-600/30"
                                  to="/"
                                >
                                  {t("nav.home")}
                                </Link>
                              )}
                              {location.pathname === "/explore" ? (
                                <span className="px-3 py-2 rounded-md bg-primary-600/30 text-white font-medium dark:bg-primary-600/30 dark:text-white">
                                  {t("nav.explore")}
                                </span>
                              ) : (
                                <Link
                                  className="px-3 py-2 rounded-md text-primary-100 hover:text-white hover:bg-primary-600/30 transition-colors dark:text-primary-100 dark:hover:text-white dark:hover:bg-primary-600/30"
                                  to="/explore"
                                >
                                  {t("nav.explore")}
                                </Link>
                              )}
                              {location.pathname === "/whats-g80" ? (
                                <span className="px-3 py-2 rounded-md bg-primary-600/30 text-white font-medium dark:bg-primary-600/30 dark:text-white">
                                  {t("nav.whats_g80")}
                                </span>
                              ) : (
                                <Link
                                  className="px-3 py-2 rounded-md text-primary-100 hover:text-white hover:bg-primary-600/30 transition-colors dark:text-primary-100 dark:hover:text-white dark:hover:bg-primary-600/30"
                                  to="/whats-g80"
                                >
                                  {t("nav.whats_g80")}
                                </Link>
                              )}
                              {!user ? (
                                <a
                                  className="text-white hover:text-primary-100 dark:text-white dark:hover:text-primary-100"
                                  href={`${
                                    AUTH_ENDPOINTS.GOOGLE_LOGIN
                                  }?returnTo=${encodeURIComponent(
                                    window.location.pathname +
                                      window.location.search
                                  )}`}
                                  onClick={(e) => {
                                    const returnPath =
                                      window.location.pathname +
                                      window.location.search;
                                    sessionStorage.setItem(
                                      "preLoginPath",
                                      returnPath
                                    );
                                    localStorage.setItem(
                                      "preLoginPath",
                                      returnPath
                                    );
                                  }}
                                >
                                  <div className="bg-white/10 text-white rounded-full px-2 py-1.5 text-sm flex items-center gap-2 hover:bg-white/20 transition-colors border border-white/20 dark:bg-white/10 dark:text-white dark:hover:bg-white/20 dark:border-white/20">
                                    <span className="text-white">
                                      {t("common.login")}
                                    </span>
                                    <img
                                      src="https://upload.wikimedia.org/wikipedia/commons/c/c1/Google_%22G%22_logo.svg"
                                      alt="Google G"
                                      className="w-4 h-4"
                                    />
                                  </div>
                                </a>
                              ) : (
                                <div className="bg-white/10 text-white rounded-full px-2 py-1.5 text-sm flex items-center gap-2 hover:bg-white/20 transition-colors border border-white/20 dark:bg-white/10 dark:text-white dark:hover:bg-white/20 dark:border-white/20">
                                  <Link
                                    to="/profile"
                                    className="font-medium text-white hover:text-primary-100 transition-colors flex items-center gap-2 dark:text-white dark:hover:text-primary-100"
                                  >
                                    <div className="w-6 h-6 rounded-full bg-gray-100 overflow-hidden flex-shrink-0 border-gray-100 border-[1px]">
                                      {user.profilePicVariants?.medium ||
                                      user.profilePic ? (
                                        <img
                                          src={
                                            user.profilePicVariants?.medium ||
                                            user.profilePic
                                          }
                                          alt={user.username}
                                          className="w-full h-full object-cover"
                                        />
                                      ) : (
                                        <div className="w-full h-full flex items-center justify-center bg-blue-50 dark:bg-blue-900">
                                          <span className="text-sm text-blue-300">
                                            {user.username
                                              .charAt(0)
                                              .toUpperCase()}
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                    {user.username}
                                    {user.spotterStats?.currentStreak?.days >
                                      0 && (
                                      <div
                                        className="fire-icon flex items-center gap-1 "
                                        title={t("common.streak_tooltip", {
                                          days: user.spotterStats.currentStreak
                                            .days,
                                        })}
                                      >
                                        <FontAwesomeIcon
                                          icon={faFire}
                                          className="text-red-400 dark:text-red-400"
                                        />
                                        <span className="text-xs font-semibold text-white">
                                          {user.spotterStats.currentStreak.days}
                                        </span>
                                      </div>
                                    )}
                                    {user.spotterStats?.streakFreezes
                                      ?.available > 0 && (
                                      <div
                                        className="freeze-icon flex items-center gap-1 "
                                        title={t("common.freeze_tooltip", {
                                          count:
                                            user.spotterStats.streakFreezes
                                              .available,
                                        })}
                                      >
                                        <FontAwesomeIcon
                                          icon={faFire}
                                          className="text-blue-300 dark:text-blue-300"
                                        />
                                        <span className="text-xs font-semibold text-white">
                                          {
                                            user.spotterStats.streakFreezes
                                              .available
                                          }
                                        </span>
                                      </div>
                                    )}
                                  </Link>
                                  <NotificationBell />
                                  <LikedPlatesButton />
                                  {user.isAdmin && (
                                    <>
                                      <span className="text-white/40">•</span>
                                      <Link
                                        to="/admin"
                                        className="text-white/70 hover:text-white dark:text-white/70 dark:hover:text-white"
                                        title={t("common.admin")}
                                      >
                                        <FontAwesomeIcon
                                          icon={faIdCard}
                                          className="text-white/70 dark:text-white/70"
                                        />
                                      </Link>
                                    </>
                                  )}
                                  <span className="text-white/40">•</span>
                                  <a
                                    className="text-white/70 hover:text-white dark:text-white/70 dark:hover:text-white"
                                    href={AUTH_ENDPOINTS.LOGOUT}
                                    title={t("common.logout")}
                                  >
                                    <FontAwesomeIcon
                                      icon={faSignOut}
                                      className="text-white/70 dark:text-white/70"
                                    />
                                  </a>
                                </div>
                              )}
                            </nav>
                          </div>
                        </div>
                      </div>
                    </header>

                    <main className="flex-1  transition-colors duration-300 dark:bg-gray-900">
                      <div className="max-w-7xl mx-auto px-4 py-6">
                        <Routes>
                          <Route path="/" element={<HomePage user={user} />} />
                          <Route
                            path="/plate/:plate"
                            element={<VehiclePage user={user} />}
                          />
                          <Route
                            path="/setup"
                            element={
                              user?.isFirstLogin ? (
                                <FirstTimeLogin
                                  user={user}
                                  onComplete={() => {
                                    setUser((prev) => ({
                                      ...prev,
                                      isFirstLogin: false,
                                    }));
                                  }}
                                />
                              ) : (
                                <Navigate to="/" replace />
                              )
                            }
                          />
                          <Route
                            path="/legal"
                            element={
                              <Suspense
                                fallback={
                                  <div className="p-4 text-center">
                                    Loading...
                                  </div>
                                }
                              >
                                <LegalPage />
                              </Suspense>
                            }
                          />
                          <Route
                            path="/profile"
                            element={
                              <ProfilePage
                                currentUser={user}
                                usernameChanged={user?.usernameChanged}
                              />
                            }
                          />
                          <Route
                            path="/community-guidelines"
                            element={
                              <Suspense
                                fallback={
                                  <div className="p-4 text-center">
                                    Loading...
                                  </div>
                                }
                              >
                                <CommunityGuidelines />
                              </Suspense>
                            }
                          />
                          <Route
                            path="/admin"
                            element={
                              user && user.isAdmin ? (
                                <AdminDashboard />
                              ) : (
                                <div className="p-4 text-center text-red-600 font-bold">
                                  Access Denied
                                </div>
                              )
                            }
                          />
                          <Route path="/explore" element={<ExplorePage />} />
                          <Route path="/whats-g80" element={<WhatsG80 />} />
                          <Route
                            path="/u/:username"
                            element={<ProfilePage />}
                          />
                          <Route path="/liked" element={<LikedPlates />} />
                          <Route path="*" element={<NotFound />} />
                        </Routes>
                      </div>
                    </main>

                    <footer className="bg-gradient-to-r from-primary-700 to-primary-800 text-white py-6 px-4 shadow-md dark:bg-gray-900">
                      <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
                        <div className="flex flex-wrap gap-6 items-center justify-center md:justify-start">
                          <Link
                            to="/legal"
                            className="text-primary-100 hover:text-white transition-colors duration-200 text-sm font-medium dark:text-primary-100 dark:hover:text-white"
                          >
                            {t("footer.legal")}
                          </Link>
                          <Link
                            to="/community-guidelines"
                            className="text-primary-100 hover:text-white transition-colors duration-200 text-sm font-medium dark:text-primary-100 dark:hover:text-white"
                          >
                            {t("footer.community_guidelines")}
                          </Link>
                          <a
                            href="https://discord.gg/kDN8mkaKAG"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary-100 hover:text-white transition-colors duration-200 flex items-center gap-1.5 text-sm font-medium dark:text-primary-100 dark:hover:text-white"
                          >
                            <svg
                              className="w-4 h-4"
                              viewBox="0 0 71 55"
                              fill="currentColor"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path d="M60.1045 4.8978C55.5792 2.8214 50.7265 1.2916 45.6527 0.41542C45.5603 0.39851 45.468 0.440769 45.4204 0.525289C44.7963 1.6353 44.105 3.0834 43.6209 4.2216C38.1637 3.4046 32.7345 3.4046 27.3892 4.2216C26.905 3.0581 26.1886 1.6353 25.5617 0.525289C25.5141 0.443589 25.4218 0.40133 25.3294 0.41542C20.2584 1.2888 15.4057 2.8186 10.8776 4.8978C10.8384 4.9147 10.8048 4.9429 10.7825 4.9795C1.57795 18.7309 -0.943561 32.1443 0.293408 45.3914C0.299005 45.4562 0.335386 45.5182 0.385761 45.5576C6.45866 50.0174 12.3413 52.7249 18.1147 54.5195C18.2071 54.5477 18.305 54.5139 18.3638 54.4378C19.7295 52.5728 20.9469 50.6063 21.9907 48.5383C22.0523 48.4172 21.9935 48.2735 21.8676 48.2256C19.9366 47.4931 18.0979 46.6 16.3292 45.5858C16.1893 45.5041 16.1781 45.304 16.3068 45.2082C16.679 44.9293 17.0513 44.6391 17.4067 44.3461C17.471 44.2926 17.5606 44.2813 17.6362 44.3151C29.2558 49.6202 41.8354 49.6202 53.3179 44.3151C53.3935 44.2785 53.4831 44.2898 53.5502 44.3433C53.9057 44.6363 54.2779 44.9293 54.6529 45.2082C54.7816 45.304 54.7732 45.5041 54.6333 45.5858C52.8646 46.6197 51.0259 47.4931 49.0921 48.2228C48.9662 48.2707 48.9102 48.4172 48.9718 48.5383C50.038 50.6034 51.2554 52.5699 52.5959 54.435C52.6519 54.5139 52.7526 54.5477 52.845 54.5195C58.6464 52.7249 64.529 50.0174 70.6019 45.5576C70.6551 45.5182 70.6887 45.459 70.6943 45.3942C72.1747 30.0791 68.2147 16.7757 60.1968 4.9823C60.1772 4.9429 60.1437 4.9147 60.1045 4.8978ZM23.7259 37.3253C20.2276 37.3253 17.3451 34.1136 17.3451 30.1693C17.3451 26.225 20.1717 23.0133 23.7259 23.0133C27.308 23.0133 30.1626 26.2532 30.1066 30.1693C30.1066 34.1136 27.28 37.3253 23.7259 37.3253ZM47.3178 37.3253C43.8196 37.3253 40.9371 34.1136 40.9371 30.1693C40.9371 26.225 43.7636 23.0133 47.3178 23.0133C50.9 23.0133 53.7545 26.2532 53.6986 30.1693C53.6986 34.1136 50.9 37.3253 47.3178 37.3253Z" />
                            </svg>
                            {t("footer.join_discord")}
                          </a>
                        </div>

                        <div className="flex flex-col items-center md:items-end">
                          <p className="text-sm text-primary-200 font-medium dark:text-primary-300">
                            {t("footer.copyright", {
                              year: new Date().getFullYear(),
                            })}
                          </p>
                          <div className="text-xs text-primary-300 mt-1 dark:text-primary-300">
                            G80 Or Not?
                          </div>
                        </div>
                      </div>
                    </footer>

                    <QuickSubmitButton user={user} />
                  </div>
                </div>
              </>
            )}
          </div>
        </ErrorBoundary>
      </div>
    </HelmetProvider>
  );
}

export default App;
